<script setup>
const {
  label,
  applicationId,
  document,
  isDisabled = false,
  customUpload = false,
  canDelete = false,
} = defineProps({
  label: String,
  applicationId: String,
  document: {
    type: Object,
    default(rawProps) {
      return {};
    },
  },
  isDisabled: Boolean,
  customUpload: Boolean,
  canDelete: Boolean,
});

const emits = defineEmits(["onFileSelectCb"]);

const optionalLabels = ref(["Resume or CV", "Recommendation letter"]);

const isLoading = ref(false);

const fileUploadError = ref("");

const toast = useToast();
const confirm = useConfirm();

const onFileSelect = async (event) => {
  isLoading.value = true;
  const file = event.files[0];
  fileUploadError.value = "";

  if (customUpload) {
    emits("onFileSelectCb", file);
    return;
  }

  console.log("upload document file", file.size);

  if (file.size > MAX_FILE_UPLOAD_SIZE) {
    fileUploadError.value = `${file.name} file too large. Please upload a file under 2MB.`;
    isLoading.value = false;
    // toast.add({
    //   severity: "error",
    //   summary: "File too large. Please upload a file under 2MB.",
    //   life: 3000,
    // });
    return;
  }

  const formData = new FormData();

  formData.append("label", label);
  formData.append("input_type", "file");
  formData.append("input_value", file);

  await useApiFetch(`/student-applications/${applicationId}/documents`, {
    method: "POST",
    body: formData,
    onResponse() {
      toast.add({
        severity: "success",
        summary: "Document is submitted.",
        life: 3000,
      });
      refreshNuxtData("student-application-detail");
    },
  });
  isLoading.value = false;
};

const fileName = computed(() => {
  return document?.media?.at(0)?.file_name.split("_")[1] || "";
});

const media = computed(() => {
  return document?.media?.at(0);
});

const onDelete = async () => {
  await useApiFetch(`student-application-documents/${document.id}/remove`, {
    method: "POST",
    onResponse() {
      toast.add({
        severity: "success",
        summary: "Successfully deleted.",
        life: 3000,
      });
      refreshNuxtData("student-application-detail");
    },
  });
};

const confirmDelete = (event) => {
  confirm.require({
    target: event.currentTarget,
    header: "Delete confirmation",
    message: "Do you want to delete this record?",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: () => {
      onDelete();
    },
    reject: () => {},
  });
};
</script>

<template>
  <div class="space-y-1 flex flex-col">
    <div class="flex items-center justify-between">
      <p class="w-2/3 md:w-auto text-[#252A31] text-sm font-medium capitalize">
        {{ label }} {{ optionalLabels.includes(label) ? " (Optional)" : "" }}
      </p>
      <div class="flex justify-between gap-2 items-center">
        <Button
          v-if="media"
          label="Open File"
          as="a"
          :href="media?.original_url"
          target="_blank"
          rel="noopener"
          link
          class="!py-0"
          :pt="{
            label: 'text-sm text-gray-800 font-medium',
          }"
        >
        </Button>
        <i
          v-if="canDelete && media?.original_url"
          class="pi pi-trash text-surface-500 dark:text-white text-sm cursor-pointer"
          @click="confirmDelete($event)"
        ></i>
      </div>
    </div>

    <div
      class="flex justify-start items-center gap-2 p-1.5 pr-3 rounded-[3px] border border-[#BAC7D5] bg-white relative"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-gray-400 bg-opacity-50 z-10 rounded-sm flex items-center justify-center"
      >
        <!-- Spinner -->
        <i
          class="pi pi-spin pi-spinner text-white"
          style="font-size: 1.5rem"
        ></i>
      </div>
      <FileUpload
        ref="fileupload"
        mode="basic"
        choose-icon="pi pi-upload"
        accept="image/jpeg, image/png, image/jpg, application/pdf"
        :maxFileSize="null"
        @select="onFileSelect"
        customUpload
        severity="secondary"
        auto
        :disabled="isDisabled"
        :pt="{
          root: {
            class: 'flex flex-nowrap text-sm items-center gap-2 truncate',
          },
          pcButton: {
            label: 'Select file',
            severity: 'secondary',
            size: 'small',
            class: 'shrink-0 !text-[#252A31] !rounded-[3px] !bg-surface-100',
          },
        }"
      />
      <p class="text-sm text-[#252A31] font-medium">
        {{ fileName }}
      </p>
    </div>
    <p class="text-sm text-rose-600 font-medium">
      {{ fileUploadError }}
    </p>
  </div>
</template>
